/**
 * @typedef {import('./product-groups-data.js').GroupProduct} GroupProduct
 */

/**
 * Loads the shared data layer via a dynamic import of its versioned asset URL
 * (Theme.assets.product_groups_data carries Shopify's `?v=` hash). A static
 * `import './product-groups-data.js'` resolves a bare relative path that the
 * CDN can't version, so a stale copy missing newer exports (e.g.
 * colourToBackground) could be served from cache. Deduped per page load.
 * @type {Promise<typeof import('./product-groups-data.js')> | null}
 */
let dataModulePromise = null;
function loadDataModule() {
  if (dataModulePromise) return dataModulePromise;
  /** @type {any} */
  const theme = /** @type {any} */ (window).Theme;
  const url = theme?.assets?.product_groups_data || './product-groups-data.js';
  dataModulePromise = import(url);
  return dataModulePromise;
}

class ProductGroupSelector extends HTMLElement {
  /** @type {string | undefined} */
  productGid;
  /** @type {string} */
  routesRoot = '/';
  /** @type {string | undefined} */
  currentHandle;
  /** @type {Element | null} */
  colorContainer = null;
  /** @type {Element | null} */
  sizeContainer = null;
  /** @type {Element | null} */
  labelEl = null;
  /** @type {boolean} */
  hideSoldOut = false;
  /** @type {number} */
  almostSoldOutThreshold = 0;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {typeof import('./product-groups-data.js') | null} */
  #data = null;

  connectedCallback() {
    /** @type {any} */
    const theme = /** @type {any} */ (window).Theme;

    this.productGid = this.dataset.productGid;
    // Strip any trailing slash so it joins cleanly with `/products/...`.
    // On localized storefronts root_url is e.g. "/en" (no trailing slash),
    // which would otherwise produce "/enproducts/...".
    this.routesRoot = (theme?.routes?.root_url || '/').replace(/\/+$/, '');
    this.currentHandle = this.dataset.productHandle;
    this.colorContainer = this.querySelector('[ref="colors"]');
    this.sizeContainer = this.querySelector('[ref="sizes"]');
    this.labelEl = this.querySelector('[ref="label"]');
    this.hideSoldOut = this.hasAttribute('data-hide-sold-out');
    this.almostSoldOutThreshold = parseInt(this.dataset.almostSoldOutThreshold || '', 10) || 0;

    this.#abortController = new AbortController();
    this.#fetchAndRender();
  }

  disconnectedCallback() {
    // The shared fetchAllGroups() promise is page-scoped and can't take a
    // per-instance signal without breaking caller dedup, so we guard the
    // render path post-await instead (see #fetchAndRender).
    this.#abortController?.abort();
  }

  async #fetchAndRender() {
    try {
      this.#data = await loadDataModule();

      let group = await this.#findProductGroup();

      // Bail out if the element was removed (e.g. theme-editor section
      // re-render) while the shared fetch was in flight.
      if (this.#abortController?.signal.aborted || !this.isConnected) return;
      if (!group || group.length <= 1) return this.#hideLabel();

      if (this.hideSoldOut) {
        group = group.filter(
          (p) => p.availableForSale || p.handle === this.currentHandle
        );
        if (group.length <= 1) return this.#hideLabel();
      }

      const currentProduct = group.find(
        (p) => p.handle === this.currentHandle
      );
      if (!currentProduct) {
        // Cached page with a renamed handle, or the current product fell out of
        // the group — swatches would all render unselected. Log for diagnosis.
        console.warn(
          '[product-group-selector] current product not found in group',
          this.currentHandle
        );
      }
      const currentColour = this.#data.parseMetafieldList(currentProduct?.colour?.value);
      const currentSize = this.#data.parseMetafieldList(currentProduct?.size?.value);
      const currentStyle = this.#data.parseMetafieldList(currentProduct?.style?.value);

      const { colors, sizes, styles } = this.#groupProducts(
        group,
        currentColour,
        currentSize,
        currentStyle
      );

      const hasColors = colors.length > 1;
      const hasStyles = styles.length > 1;
      const hasSizes = sizes.length > 1;
      if (!hasColors && !hasStyles && !hasSizes) {
        // A multi-product group resolved to no renderable variants — usually a
        // misconfigured colour/size/style metafield (wrong namespace/key).
        console.warn(
          '[product-group-selector] group has >1 product but no colour/size/style variants to render',
          this.currentHandle
        );
        return this.#hideLabel();
      }

      // Batch every DOM write into one frame so the browser lays out once.
      requestAnimationFrame(() => {
        if (this.#abortController?.signal.aborted || !this.isConnected) return;

        if (hasColors) this.#renderColors(colors, currentColour);

        // Style takes precedence over size: a group either varies by style or
        // by size within the current colour, not both at once.
        if (hasStyles) {
          this.#renderVariants(styles);
          this.#updateLabel(styles.length);
        } else if (hasSizes) {
          this.#renderVariants(sizes);
          this.#updateLabel(sizes.length);
        }

        this.classList.add('product-group-selector--loaded');
      });
    } catch (error) {
      console.error('[product-group-selector]', error);
    }
  }

  /**
   * @param {GroupProduct[]} products
   * @param {string | null} currentColour
   * @param {string | null} currentSize
   * @param {string | null} currentStyle
   */
  #groupProducts(products, currentColour, currentSize, currentStyle) {
    const colorsWithBestMatch = new Map();
    const sizeMap = new Map();
    const styleMap = new Map();

    for (const product of products) {
      const colour = this.#data.parseMetafieldList(product.colour?.value);
      const size = this.#data.parseMetafieldList(product.size?.value);
      const style = this.#data.parseMetafieldList(product.style?.value);

      // Colors: one product per colour, prefer one matching current size/style
      if (colour) {
        const matchesCurrent = size === currentSize || style === currentStyle;
        if (matchesCurrent || !colorsWithBestMatch.has(colour)) {
          colorsWithBestMatch.set(colour, product);
        }
      }

      // Sizes and styles: only products in the current colour
      if (colour === currentColour) {
        if (size) sizeMap.set(size, product);
        if (style) styleMap.set(style, product);
      }
    }

    return {
      colors: Array.from(colorsWithBestMatch, ([colour, product]) => ({
        colour,
        product,
      })),
      sizes: Array.from(sizeMap, ([label, product]) => ({
        label,
        product,
      })),
      styles: Array.from(styleMap, ([label, product]) => ({
        label,
        product,
      })),
    };
  }

  /**
   * @param {number} count
   */
  #updateLabel(count) {
    if (!(this.labelEl instanceof HTMLElement)) return;
    this.labelEl.textContent = this.labelEl.textContent.replace(
      '{count}',
      String(count)
    );
    this.labelEl.style.display = '';
  }

  /** Hide the label so a prior render's count doesn't linger when, on a
   *  re-render, the selector resolves to nothing to show. */
  #hideLabel() {
    if (this.labelEl instanceof HTMLElement) {
      this.labelEl.style.display = 'none';
    }
  }

  /**
   * @param {Array<{colour: string, product: GroupProduct}>} colors
   * @param {string | null} currentColour
   */
  #renderColors(colors, currentColour) {
    if (!this.colorContainer) return;

    const fragment = document.createDocumentFragment();

    for (const { colour, product } of colors) {
      const isCurrent = colour === currentColour;
      const isSoldOut = product.availableForSale === false;
      const link = document.createElement('a');
      link.href = this.#productUrl(product.handle);
      link.className = `product-group-selector__color${isCurrent ? ' product-group-selector__color--active' : ''}${isSoldOut ? ' product-group-selector__color--sold-out' : ''}`;
      link.setAttribute('aria-label', colour);
      if (isCurrent) link.setAttribute('aria-current', 'true');

      const swatch = document.createElement('span');
      swatch.className = 'product-group-selector__color-swatch';
      swatch.style.background = this.#data.colourToBackground(colour);
      link.appendChild(swatch);

      fragment.appendChild(link);
    }

    this.colorContainer.appendChild(fragment);
  }

  /**
   * Renders size or style variants — they share the same image-button layout.
   * @param {Array<{label: string, product: GroupProduct}>} variants
   */
  #renderVariants(variants) {
    if (!this.sizeContainer) return;

    const fragment = document.createDocumentFragment();

    for (const { label: variantLabel, product } of variants) {
      const isCurrent = product.handle === this.currentHandle;
      const link = document.createElement('a');
      link.href = this.#productUrl(product.handle);
      link.className = `product-group-selector__item${isCurrent ? ' product-group-selector__item--active' : ''}`;
      link.setAttribute('aria-label', `${product.title} - ${variantLabel}`);
      if (isCurrent) link.setAttribute('aria-current', 'true');

      if (product.featuredImage?.url) {
        const imageWrap = document.createElement('div');
        imageWrap.className = 'product-group-selector__image';
        const img = document.createElement('img');
        img.src = product.featuredImage.url;
        img.alt = product.featuredImage.altText || product.title;
        img.loading = 'lazy';
        // Match the CSS image box (56x70) so the browser reserves the correct
        // aspect ratio while the thumbnail loads.
        img.width = 56;
        img.height = 70;
        imageWrap.appendChild(img);
        link.appendChild(imageWrap);
      }

      const name = document.createElement('span');
      name.className = 'product-group-selector__name';
      name.textContent = variantLabel;
      link.appendChild(name);

      if (
        this.almostSoldOutThreshold > 0 &&
        product.availableForSale &&
        product.totalInventory != null &&
        product.totalInventory > 0 &&
        product.totalInventory <= this.almostSoldOutThreshold
      ) {
        const indicator = document.createElement('span');
        indicator.className = 'product-group-selector__indicator';
        link.appendChild(indicator);
      }

      fragment.appendChild(link);
    }

    this.sizeContainer.appendChild(fragment);
  }

  /**
   * @param {string} handle
   * @returns {string}
   */
  #productUrl(handle) {
    return `${this.routesRoot}/products/${handle}`;
  }

  /**
   * A product can appear in more than one product_group metaobject (e.g. a
   * narrow legacy group plus the full one). Scan all groups and return the
   * largest matching one so size/style variants aren't lost.
   * @returns {Promise<GroupProduct[] | null>}
   */
  async #findProductGroup() {
    const groups = await this.#data.fetchAllGroups();

    /** @type {GroupProduct[] | null} */
    let bestMatch = null;
    for (const products of groups) {
      const containsCurrent = products.some((p) => p.id === this.productGid);
      if (containsCurrent && (!bestMatch || products.length > bestMatch.length)) {
        bestMatch = products;
      }
    }

    return bestMatch;
  }
}

if (!customElements.get('product-group-selector')) {
  customElements.define('product-group-selector', ProductGroupSelector);
}
